import '../main';
import { Extension } from '@tiptap/core';
import { parseStyleString, responsiveVarName, stringifyStyleObject } from '@/plugins/tiptap-v3/functionality/responsive/responsive';
function getBreakpoint(editor) {
    var _a, _b;
    return ((_b = (_a = editor === null || editor === void 0 ? void 0 : editor.storage) === null || _a === void 0 ? void 0 : _a.responsiveEngine) === null || _b === void 0 ? void 0 : _b.breakpoint) || 'lg';
}
function getResponsiveConfig(options) {
    if (!options.responsive || !options.responsive.enabled)
        return null;
    return {
        prefix: options.responsive.prefix || 'vp',
        cssPropFontSize: options.responsive.cssPropFontSize || 'font-size',
    };
}
/**
 * This extension allows you to set a font size for text.
 * @see https://www.tiptap.dev/api/extensions/font-size
 */
export const FontSize = Extension.create({
    name: 'fontSize',
    addOptions() {
        return {
            types: ['textStyle'],
            responsive: {
                enabled: true,
                prefix: 'vp',
                cssPropFontSize: 'font-size',
            },
        };
    },
    addGlobalAttributes() {
        const cfg = getResponsiveConfig(this.options);
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        parseHTML: element => element.style.fontSize,
                        renderHTML: attributes => {
                            if (!attributes.fontSize) {
                                return {};
                            }
                            // In responsive mode we keep font-size as CSS vars on `style`.
                            // Avoid emitting a fixed inline font-size from this attribute.
                            if (cfg) {
                                return {};
                            }
                            return {
                                style: `font-size: ${attributes.fontSize}`,
                            };
                        },
                    },
                },
            },
        ];
    },
    addCommands() {
        return {
            setFontSize: (fontSize, opts) => ({ editor, chain }) => {
                const cfg = getResponsiveConfig(this.options);
                // Non-responsive behavior (backwards compatible)
                if (!cfg) {
                    return chain().setMark('textStyle', { fontSize }).run();
                }
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const fsVar = responsiveVarName(cfg.cssPropFontSize, bp, cfg.prefix);
                const attrs = editor.getAttributes('textStyle') || {};
                const styleObj = parseStyleString(attrs.style || '');
                // Legacy compatibility: remove fixed font-size so responsive vars are authoritative.
                delete styleObj['font-size'];
                styleObj[fsVar] = fontSize;
                return chain()
                    .setMark('textStyle', {
                    ...attrs,
                    style: stringifyStyleObject(styleObj),
                    fontSize: null,
                })
                    .run();
            },
            unsetFontSize: (opts) => ({ editor, chain }) => {
                const cfg = getResponsiveConfig(this.options);
                // Non-responsive behavior
                if (!cfg) {
                    return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
                }
                const bp = (opts === null || opts === void 0 ? void 0 : opts.breakpoint) || getBreakpoint(editor);
                const fsVar = responsiveVarName(cfg.cssPropFontSize, bp, cfg.prefix);
                const attrs = editor.getAttributes('textStyle') || {};
                const styleObj = parseStyleString(attrs.style || '');
                delete styleObj['font-size'];
                delete styleObj[fsVar];
                return chain()
                    .setMark('textStyle', {
                    ...attrs,
                    style: stringifyStyleObject(styleObj),
                    fontSize: null,
                })
                    .removeEmptyTextStyle()
                    .run();
            },
        };
    },
});
